import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShoppingCart, Handshake, Clock, Zap, MapPin, ArrowLeft, ArrowRight, Lock, Settings, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import B2BTradeRulesModal from "./B2BTradeRulesModal";

/**
 * Jun-2026 (User-Wunsch) — Direkt-Aktionen für FREMDE Fahrzeuge im Dispo-Scope
 * „Alle Händler" (ohne Umweg über die Detailseite):
 *   kind="reserve" → B2B-Reservierung (48h)
 *   kind="deal"    → Kauf ODER Vermittlung (Auswahl)
 *
 * Jede Aktion löst beim Besitzer + beim Auslösenden eine E-Mail UND einen
 * Eintrag in „Was zu tun ist" aus (Backend).
 */
export default function B2BForeignActionModal({ item, kind, onClose, onDone }) {
    const navigate = useNavigate();
    const veh = item.title || `${item.brand || ""} ${item.model || ""}`.trim() || "Fahrzeug";
    const owner = item.owner_company || "anderem Händler";
    // Jun-2026 (User-Wunsch) — vollständiger Standort des Fahrzeugs (Straße + PLZ + Ort).
    const locStreet = item.owner_street || item.street || "";
    const locCity = [item.owner_postal_code, item.owner_city].filter(Boolean).join(" ");
    const locationLine = [locStreet, locCity].filter(Boolean).join(", ");
    const [busy, setBusy] = useState(false);
    // reserve
    const [hours, setHours] = useState(48);
    const [message, setMessage] = useState("");
    // deal
    const [mode, setMode] = useState("kauf");
    const [handoverDate, setHandoverDate] = useState("");
    // Jul-2026 (User-Wunsch) — Zahnrad: Übersicht, was bei der Auswahl berücksichtigt wird.
    const [rulesOpen, setRulesOpen] = useState(false);
    // Aug-2026 (User-Wunsch) — eigene Kauf/Vermittlung-Regeln direkt aus dem Zahnrad bearbeiten
    const [myRulesOpen, setMyRulesOpen] = useState(false);
    // Jul-2026 (User-Wunsch) — erlaubte Modi + automatische Beträge vom Server
    // (Zahnrad-Regeln des Verkäufers, Gutschrift-/Provisions-Berechnung).
    const [opts, setOpts] = useState(null);
    // Aug-2026 (Testing-Agent): kein „Button-Flash" — solange die Handelsregeln
    // laden (oder fehlschlagen), werden KEINE Handels-Buttons angezeigt.
    const [optsError, setOptsError] = useState(false);
    useEffect(() => {
        if (kind !== "deal") return;
        api.get(`/dealer/b2b/${item.id}/trade-options`)
            .then((r) => {
                setOpts(r.data);
                if (!r.data.kauf_allowed && r.data.vermittlung_allowed) setMode("vermittlung");
            })
            .catch(() => { setOpts(null); setOptsError(true); });
    }, [item.id, kind]);
    // Jul-2026 (Bugfix) — window.confirm wird in eingebetteten Umgebungen blockiert;
    // stattdessen Inline-Bestätigung per zweitem Klick.
    const [confirmBuy, setConfirmBuy] = useState(false);
    const priceB2B = item.price_b2b || item.price || null;

    const submitReserve = async () => {
        setBusy(true);
        try {
            await api.post(`/dealer/b2b/${item.id}/reserve`, { hours: Number(hours) || 48, message: message.trim() || undefined });
            toast.success(`„${veh}" für ${hours}h reserviert · Besitzer & Aufgabe informiert`);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reservierung fehlgeschlagen");
        } finally { setBusy(false); }
    };

    const submitDeal = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/dealer/b2b/${item.id}/deal`, {
                mode,
                message: message.trim() || undefined,
            });
            toast.success(mode === "kauf"
                ? `Kaufanfrage für „${veh}" gesendet · Besitzer & Aufgabe informiert`
                : `Vermittlung von „${veh}" gestartet · weiter zu „Käufer-Stammdaten ergänzen"`);
            onDone?.();
            // Jul-2026 (User-Wunsch, UI-Merge): Vermittlung führt DIREKT in den
            // regulären 6-Schritte-Verkaufsprozess — die Endkunden-Daten werden
            // dort in „Käufer-Stammdaten ergänzen (Schritt 1 von 6)" erfasst
            // (keine doppelte Eingabe mehr im B2B-Popup).
            if (mode === "vermittlung" && data?.start_sale) {
                navigate(`/edit-listing/${data.listing_id || item.id}?focus=disposition&dispo=sell`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally { setBusy(false); }
    };

    // Jun-2026 (User-Wunsch) — „Verbindlich kaufen": Sofortkauf, Bestandskopie beim
    // Käufer, Verkäufer-Fahrzeug verkauft, anschließend direkt B2C-Verkauf starten.
    const submitBuyNow = async () => {
        if (!confirmBuy) { setConfirmBuy(true); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/dealer/b2b/${item.id}/buy-now`, {
                message: message.trim() || undefined,
                handover_date: handoverDate || undefined,
            });
            toast.success(`„${veh}" verbindlich gekauft · in deinen Bestand übernommen`);
            onDone?.();
            if (data?.new_listing_id) {
                // Enhancement (Jun-2026): direkt die vorausgefüllte Endkunden-Maske (B2C)
                // öffnen (?dispo=sell) statt nur in die Disposition zu springen.
                navigate(`/edit-listing/${data.new_listing_id}?focus=disposition&dispo=sell`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verbindlicher Kauf fehlgeschlagen");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl w-[95vw] max-h-none" data-testid="b2b-foreign-action-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {kind === "reserve" ? <Clock className="w-5 h-5 text-[#FFCC00]" /> : <Handshake className="w-5 h-5 text-[#FFCC00]" />}
                        {kind === "reserve" ? "B2B-Reservierung" : "Kauf / Vermittlung"}
                        {kind === "deal" && (
                            <button type="button" onClick={() => setRulesOpen(v => !v)}
                                title="Was wird bei der Auswahl berücksichtigt?"
                                data-testid="b2b-deal-rules-gear"
                                className={`ml-1 inline-flex items-center justify-center w-7 h-7 rounded-sm border transition-colors ${rulesOpen ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "border-gray-300 text-gray-500 hover:border-[#111827] hover:text-[#111827]"}`}>
                                <Settings className="w-4 h-4" />
                            </button>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {veh} · <strong>{owner}</strong>{item.owner_city ? ` (${item.owner_city})` : ""}
                        {locationLine && (
                            <span className="mt-1 flex items-center gap-1 text-[12px] text-gray-600" data-testid="b2b-foreign-location">
                                <MapPin className="w-3.5 h-3.5 shrink-0" /> {locationLine}
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {kind === "reserve" ? (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Reservierungsdauer (Stunden)</Label>
                            <Input type="number" min="1" max="720" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="b2b-reserve-hours" />
                        </div>
                        <div>
                            <Label className="text-xs">Nachricht an den Besitzer (optional)</Label>
                            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="b2b-reserve-message" />
                        </div>
                        <p className="text-[11px] text-gray-500">Das Fahrzeug wird sofort reserviert; der Besitzer-Händler erhält E-Mail + Aufgabe.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rulesOpen && (
                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-[12px] text-gray-700 space-y-2" data-testid="b2b-deal-rules-panel">
                                <div className="flex items-center justify-between">
                                    <p className="font-black text-[11px] uppercase tracking-wider text-gray-500">Das wird bei deiner Auswahl berücksichtigt</p>
                                    <button type="button" onClick={() => setRulesOpen(false)} className="text-gray-400 hover:text-gray-700" data-testid="b2b-deal-rules-close"><X className="w-4 h-4" /></button>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Freigabe des Verkäufers:</b> {opts
                                            ? (opts.kauf_allowed && opts.vermittlung_allowed ? "Kauf UND Vermittlung erlaubt"
                                                : opts.kauf_allowed ? "nur Kauf erlaubt" : opts.vermittlung_allowed ? "nur Vermittlung erlaubt" : "aktuell nichts freigegeben")
                                            : "wird geladen…"}{opts?.restriction_rule ? ` (Regel: ${opts.restriction_rule})` : ""}</span></p>
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Ankaufs-Gutschrift (bei Kauf):</b> {opts?.purchase_credit_eur ? formatPrice(opts.purchase_credit_eur) : "—"}
                                            {opts?.purchase_credit_rule ? ` — ${opts.purchase_credit_rule}` : ""}. Wird bei „Verbindlich kaufen" automatisch in deinem Branding an den Verkäufer erstellt (PDF-Entwurf unter Rechnungen).</span></p>
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Bestand:</b> Bei „Verbindlich kaufen" wird das Fahrzeug beim Verkäufer sofort als verkauft markiert und als 1:1-Kopie in deinen Bestand übernommen — du kannst den B2C-Verkauf direkt starten.</span></p>
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Übergabe/Transport:</b> Dein Wunschtermin legt beim Verkäufer automatisch eine Übergabe-/Transportaufgabe an.</span></p>
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Vermittlungsprovision:</b> {opts?.provision_eur ? formatPrice(opts.provision_eur) : "nach Vereinbarung"}
                                            {opts?.provision_rule ? ` — ${opts.provision_rule}` : ""}. Die Provisionsgutschrift wird automatisch erstellt, sobald der Endkunden-Verkauf abgeschlossen ist.</span></p>
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Vermittlung:</b> Rechnung, Zahlung und Zulassung laufen im Branding des Besitzers; du erfasst nur die Käufer-Stammdaten im 6-Schritte-Verkaufsprozess.</span></p>
                                    <p className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                        <span><b>Benachrichtigungen:</b> Bei jeder Aktion erhalten Besitzer und du automatisch E-Mail + Aufgabe in „Was zu tun ist" (inkl. Button zum Fahrzeug).</span></p>
                                </div>
                                {/* Aug-2026 (User-Wunsch) — Regel-Verwaltung direkt aus dem Zahnrad erreichbar */}
                                <div className="pt-2 border-t border-gray-200">
                                    <button type="button" onClick={() => setMyRulesOpen(true)}
                                        className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#111827] border border-gray-300 rounded-sm px-2.5 py-1.5 hover:border-[#111827] hover:bg-[#FFCC00]/20 transition-colors"
                                        data-testid="b2b-deal-open-my-rules">
                                        <Settings className="w-3.5 h-3.5" /> Meine Kauf/Vermittlung-Regeln bearbeiten
                                    </button>
                                    <p className="text-[10px] text-gray-400 mt-1">Legt fest, welche Buttons ANDERE Händler bei DEINEN Fahrzeugen sehen (je Marke/Modell/Fahrzeugtyp/Standtage). Die Freigabe hier oben bestimmt der Verkäufer dieses Fahrzeugs.</p>
                                </div>
                            </div>
                        )}
                        {/* Jul-2026 (User-Wunsch) — Preis & B2B-Konditionen direkt im Popup */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-3" data-testid="b2b-deal-pricing">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Preis B2B (Einkauf)</p>
                                <p className="text-base font-black text-[#111827]" data-testid="b2b-deal-price-b2b">{priceB2B ? formatPrice(priceB2B) : "—"}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Preis B2C (Privat)</p>
                                <p className="text-base font-bold text-gray-700" data-testid="b2b-deal-price-b2c">{item.price ? formatPrice(item.price) : "—"}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">UVP (Liste)</p>
                                <p className="text-base font-bold text-gray-700" data-testid="b2b-deal-price-uvp">{item.list_price_gross ? formatPrice(item.list_price_gross) : "—"}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{mode === "vermittlung" ? "Vermittlungsprovision" : "Ankaufs-Gutschrift (autom.)"}</p>
                                <p className="text-base font-bold text-green-700" data-testid="b2b-deal-provision">
                                    {mode === "vermittlung"
                                        ? (opts?.provision_eur ? formatPrice(opts.provision_eur) : "n. Vereinbarung")
                                        : (opts?.purchase_credit_eur ? formatPrice(opts.purchase_credit_eur) : "—")}
                                </p>
                                <p className="text-[9px] text-gray-400 leading-tight" data-testid="b2b-deal-rule">
                                    {mode === "vermittlung" ? (opts?.provision_rule || "") : (opts?.purchase_credit_rule || "")}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2" data-testid="b2b-deal-mode">
                            {/* Aug-2026 (User-Wunsch): nicht freigegebene Option KOMPLETT ausblenden
                                statt ausgegraut anzuzeigen — nur der erlaubte Button erscheint. */}
                            {!opts && !optsError && (
                                <p className="text-[11px] text-gray-400 inline-flex items-center gap-1.5 py-2" data-testid="b2b-deal-options-loading">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Handelsregeln werden geprüft …
                                </p>
                            )}
                            {optsError && (
                                <p className="text-[11px] text-red-600 font-bold py-2" data-testid="b2b-deal-options-error">
                                    Handelsregeln konnten nicht geladen werden — bitte das Fenster schließen und erneut öffnen.
                                </p>
                            )}
                            {opts && [
                                { key: "kauf", label: "Kauf (selbst)", Icon: ShoppingCart, allowed: !!opts.kauf_allowed },
                                { key: "vermittlung", label: "Vermittlung (an Endkunde)", Icon: Handshake, allowed: !!opts.vermittlung_allowed },
                            ].filter(({ allowed }) => allowed).map(({ key, label, Icon }) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setMode(key)}
                                    className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2 py-2 rounded-sm border transition-colors ${
                                        mode === key ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                                    }`}
                                    data-testid={`b2b-deal-mode-${key}`}
                                >
                                    <Icon className="w-3.5 h-3.5" /> {label}
                                </button>
                            ))}
                            {opts && !opts.kauf_allowed && !opts.vermittlung_allowed && (
                                <p className="text-[11px] text-red-600 font-bold" data-testid="b2b-deal-nothing-allowed">
                                    Der Verkäufer hat für dieses Fahrzeug aktuell weder Kauf noch Vermittlung freigegeben.
                                </p>
                            )}
                        </div>
                        <p className="text-[11px] text-gray-500" data-testid="b2b-deal-hint">
                            {mode === "kauf"
                                ? "Bei „Verbindlich kaufen“ wird SOFORT eine Ankaufs-Gutschrift in deinem Branding an den Verkäufer erstellt und das Fahrzeug in deinen Bestand übernommen."
                                : "Der Verkaufsprozess läuft im Branding des verkaufenden Händlers. Deine Provisionsrechnung wird automatisch erstellt, sobald der Endkunden-Verkauf abgeschlossen ist."}
                        </p>
                        {mode === "kauf" && (
                            <div data-testid="b2b-buynow-handover">
                                <Label className="text-xs">Wunschtermin Übergabe / Abholung (optional)</Label>
                                <Input type="date" value={handoverDate} onChange={(e) => setHandoverDate(e.target.value)} data-testid="b2b-buynow-handover-date" />
                                <p className="text-[11px] text-gray-500 mt-1">Nur bei „Verbindlich kaufen": legt beim Verkäufer eine Übergabe-/Transportaufgabe mit diesem Termin an.</p>
                            </div>
                        )}
                        {mode === "vermittlung" && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-[12px] text-emerald-900 flex items-start gap-2" data-testid="b2b-vermittlung-merge-hint">
                                <ArrowRight className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                                <span>
                                    <b>Endkunden-Daten folgen im nächsten Schritt:</b> Nach „Vermittlung starten“ öffnet sich der reguläre
                                    Verkaufsprozess mit <b>„Käufer-Stammdaten ergänzen — Schritt 1 von 6“</b> (inkl. Bestandskunden-Suche).
                                    Keine doppelte Eingabe nötig.
                                </span>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Nachricht an den Besitzer (optional)</Label>
                            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="b2b-deal-message" />
                        </div>
                    </div>
                )}

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    {confirmBuy && (
                        <p className="text-[11px] font-bold text-[#B45309] sm:mr-auto self-center" data-testid="b2b-buy-now-confirm-hint">
                            Sofortkauf{priceB2B ? ` zu ${formatPrice(priceB2B)}` : ""} — Fahrzeug wird in deinen Bestand übernommen. Zum Bestätigen erneut klicken.
                        </p>
                    )}
                    <Button variant="outline" onClick={onClose} data-testid="b2b-deal-back"><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
                    {kind === "deal" && mode === "kauf" && (
                        <Button
                            onClick={submitBuyNow}
                            disabled={busy || !opts || !opts.kauf_allowed}
                            className={`font-semibold ${confirmBuy ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#FF8300] hover:bg-[#E6760A] text-[#09090B]"}`}
                            data-testid="b2b-buy-now-submit"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Zap className="w-4 h-4 mr-1" /> {confirmBuy ? "Ja, verbindlich kaufen!" : "Verbindlich kaufen"}</>)}
                        </Button>
                    )}
                    {/* Jul-2026 (User-Wunsch): „Kaufanfrage senden" vorerst ausgegraut/ohne Funktion. */}
                    <Button
                        onClick={kind === "reserve" ? submitReserve : submitDeal}
                        disabled={busy || (kind === "deal" && (mode === "kauf" || !opts))}
                        title={kind === "deal" && mode === "kauf" ? "Kaufanfrage aktuell deaktiviert — bitte „Verbindlich kaufen“ nutzen." : undefined}
                        data-testid="b2b-action-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (kind === "reserve" ? "Reservieren" : (mode === "kauf" ? "Kaufanfrage senden" : "Vermittlung starten"))}
                    </Button>
                </DialogFooter>
                {myRulesOpen && <B2BTradeRulesModal onClose={() => setMyRulesOpen(false)} />}
            </DialogContent>
        </Dialog>
    );
}
